import { IAPIResponseError, IError } from '@checkout/errors';
import { IAddToCartSuccessPayload, ILineItemAddSuccessPayload, ILineItemRemoveSuccessPayload } from '@checkout/payloadTypes';
import { RootState } from '@checkout/state';
import { AxiosResponse } from 'axios';
import { Epic, ofType } from 'redux-observable';
import { Observable } from 'rxjs/Observable';
import { concat as concat$ } from 'rxjs/observable/concat';
import { fromPromise as fromPromise$ } from 'rxjs/observable/fromPromise';
import { of as of$ } from 'rxjs/observable/of';
import { catchError, concat, debounceTime, delay, filter, flatMap, mergeMap, takeUntil, tap } from 'rxjs/operators';
import { checkoutApi } from 'ys-analytics';
import ActionTypes from '../actions/actionTypes';
import { AddToCartSuccessAction, RootAction } from '../actions/actionTypes.d';
import {
  addLineItemSuccess,
  addToCartSuccess, checkoutRequest,
  closeCart,
  countryChangeRequest,
  countryStateChangeRequest,
  fetchKCOWidgetSuccess,
  openCart,
  preselectInitialValues,
  removeLineItemSuccess,
  resetErrors,
  responseError,
  setErrors
} from '../reducers/checkout';
import { rest } from '../utils/apiUtils';
import convertMessages from '../utils/convertErrors';
import { FormNames } from '../utils/formNames';
import { logger } from '../utils/logger';
import { isAdyenCheckout } from '../utils/paymentMethods';
import { ingridResume, ingridSuspend, isShipwallet2, kcoResume, kcoSuspend } from '../utils/widgets';
import { AddToCartRequestAction, DecreaseQuantityRequestAction, IncreaseQuantityRequestAction } from './../actions/actionTypes.d';

const { eeAddToCart, eeRemoveFromCart, fbAddToCart, findifyUpdateCart } = checkoutApi;

export const addToCartEpic: Epic<RootAction, RootAction, RootState> = (action$, state$): Observable<RootAction> =>
  action$.pipe(
    ofType(ActionTypes.ITEM_ADD_REQUEST),
    tap(() => { kcoSuspend(); ingridSuspend(); }),
    mergeMap((action: AddToCartRequestAction) =>
    fromPromise$(rest
      .httpPost(`/items/${action.payload.itemId}/quantity/${action.payload.quantity}`, {
        quantity: action.payload.quantity,
        productUrl: window.location.href,
      }))
      .pipe(
        flatMap((payload: AxiosResponse<IAddToCartSuccessPayload>) => {
        kcoResume();
        ingridResume();
        if (payload && payload.data && payload.data.selection && state$.value && state$.value.checkout && state$.value.checkout.selection) {
          try {
            fbAddToCart(payload.data.selection.items, state$.value.checkout.selection.items, payload.data.selection.currency);
            findifyUpdateCart(payload.data.selection.items, 'id');
          } catch (err) {
            // tslint:disable-next-line:no-console
            console.error('Enhanced Ecommerce error: ', err);
          }
          try {
            eeAddToCart(payload.data.selection.items, state$.value.checkout.selection.items, payload.data.selection.currency);
          } catch (err) {
            // tslint:disable-next-line:no-console
            console.error('Enhanced Ecommerce error: ', err);
          }
        }
        if (!state$.value.checkout.token) {
          return concat$(
            of$(addToCartSuccess(payload.data)),
            of$(preselectInitialValues(payload.data))
          );
        }
        return of$(addToCartSuccess(payload.data));
      }),
      catchError((error: IError) => {
        kcoResume();
        ingridResume();
        logger(error);
        if (error.response && error.response.data) {
          const convertedMessages = convertMessages(error.response.data, state$.value.checkout.intlData);
          const responseApiErrorPayload: IAPIResponseError = {
            errors: convertedMessages,
            selection: error.response.data.selection,
            unavailable: error.response.data.unavailable,
          };
          return of$(responseError(responseApiErrorPayload));
        }
        return of$(setErrors(error));
      })
    )
  )
);

// Check if country from location exists in countries list array and if not call country PUT with first country from the countries list
/* ***
  Urgent: do not rely on location.shipTo because Centra returns there location.shipTo=false for countries to which they can send to
*** */
export const checkShipToCountryEpic: Epic<RootAction, RootAction, RootState> = (action$, state$): Observable<RootAction> =>
  action$.pipe(
    ofType(ActionTypes.ITEM_ADD_SUCCESS),
    mergeMap((action: AddToCartSuccessAction) => {
      const firstCountryFromTheList =
        action.payload &&
        action.payload.countries &&
        action.payload.countries.length > 0 &&
        action.payload.countries[0];
      const isCountryAvailable = action.payload &&
        action.payload.location &&
        action.payload.countries &&
        action.payload.countries.length > 0 &&
        action.payload.countries.find(country => country.country === action.payload.location.country);

      if (firstCountryFromTheList && action.payload && !isCountryAvailable) {
        if (firstCountryFromTheList.states) {
          return of$(countryStateChangeRequest({
            countryCode: firstCountryFromTheList.country,
            formName: FormNames.shippingAddress,
            stateCode: firstCountryFromTheList.states[0].state
          }));
        } else {
          return of$(countryChangeRequest({
            countryCode: firstCountryFromTheList.country,
            formName: FormNames.shippingAddress,
            language: action.payload.location.language
          }));
        }
      }
      return of$();
    })
    );

export const blinkCartEpic: Epic<RootAction, RootAction, RootState> = (action$, state$): Observable<RootAction> =>
  action$.pipe(
    ofType(ActionTypes.ITEM_ADD_SUCCESS),
    mergeMap(() => of$(openCart())
      .pipe(
        concat(of$(closeCart())
        .pipe(
          delay(state$.value.checkout.ui.cart.blinkDelay),
          takeUntil(action$.ofType(ActionTypes.MOUSE_ENTER_CART, ActionTypes.TOGGLE_CART_VISIBILITY)))
        )
      )
    )
  );

export const addLineItemEpic: Epic<RootAction, RootAction, RootState> = (action$, state$): Observable<RootAction> =>
  action$.pipe(
    ofType(ActionTypes.LINE_ITEM_ADD_REQUEST),
    debounceTime(300),
    tap(() => {
      if (state$.value.checkout.selection.pluginFields && isShipwallet2(state$.value.checkout.selection.pluginFields.shipwallet)) {
        ingridSuspend();
      }
      kcoSuspend();
    }),
    mergeMap((action: IncreaseQuantityRequestAction) => fromPromise$(
      rest.httpPost(`/lines/${action.payload.line}/quantity/${action.payload.quantity}`))
        .pipe(
          flatMap((payload: AxiosResponse<ILineItemAddSuccessPayload>) => {
            if (state$.value.checkout.selection.pluginFields && isShipwallet2(state$.value.checkout.selection.pluginFields.shipwallet)) {
              ingridResume();
            }
            kcoResume();
            if (payload && payload.data && payload.data.selection && state$.value && state$.value.checkout && state$.value.checkout.selection) {
              try {
                fbAddToCart(payload.data.selection.items, state$.value.checkout.selection.items, payload.data.selection.currency);
                findifyUpdateCart(payload.data.selection.items, 'id');
              } catch (err) {
                // tslint:disable-next-line:no-console
                console.error('Enhanced Ecommerce error: ', err);
              }
              try {
                eeAddToCart(payload.data.selection.items, state$.value.checkout.selection.items, payload.data.selection.currency);
              } catch (err) {
                // tslint:disable-next-line:no-console
                console.error('Enhanced Ecommerce error: ', err);
              }
            }

            const { paymentMethod: selectedPaymentMethod } = state$.value.checkout.selection;
            const isAdyenCheckoutSelected = isAdyenCheckout(selectedPaymentMethod);

            const adyenOfs = isAdyenCheckoutSelected ? [of$(checkoutRequest())] : [];

            // This is for age limit functionality (needs to replace KCO snipped in some cases)
            if (payload.data && payload.data.selection && payload.data.selection.pluginFields &&
                payload.data.selection.pluginFields.klarnaReplaceSnippet !== undefined &&
                payload.data.selection.pluginFields && payload.data.selection.pluginFields.klarnaReplaceSnippet &&
                payload.data.selection.pluginFields && payload.data.selection.pluginFields.paymentHTML) {

              if (window.location.pathname.includes('selection') && isAdyenCheckoutSelected) {
                return concat$(
                  of$(fetchKCOWidgetSuccess(Object.assign({}, { formHtml: payload.data.selection.pluginFields.paymentHTML }, { selectedPaymentMethod }))),
                  of$(addLineItemSuccess(payload.data)),
                  ...adyenOfs
                );
              }

              return concat$(
                of$(fetchKCOWidgetSuccess(Object.assign({}, { formHtml: payload.data.selection.pluginFields.paymentHTML }, { selectedPaymentMethod }))),
                of$(addLineItemSuccess(payload.data))
              );
            }

            if (window.location.pathname.includes('selection') && isAdyenCheckoutSelected) {
              return concat$(
                of$(addLineItemSuccess(payload.data)),
                ...adyenOfs
              );
            }

            return concat$(of$(addLineItemSuccess(payload.data)));
          }),
          catchError((error: IError) => {
            kcoResume();
            ingridResume();
            logger(error);
            // TODO - if "404 Not Found" clear selection or this specific item ?
            return of$(setErrors(error));
        })
      )
    )
);

export const removeLineItemEpic: Epic<RootAction, RootAction, RootState> = (action$, state$): Observable<RootAction> =>
  action$.pipe(
    ofType(ActionTypes.LINE_ITEM_REMOVE_REQUEST),
    debounceTime(300),
    tap(() => {
      if (state$.value.checkout.selection.pluginFields && isShipwallet2(state$.value.checkout.selection.pluginFields.shipwallet)) {
        ingridSuspend();
      }
      kcoSuspend();
    }),
    mergeMap((action: DecreaseQuantityRequestAction) => fromPromise$(
      rest.httpDelete(`/lines/${action.payload.line}/quantity/${action.payload.quantity}`))
        .pipe(
          flatMap((payload: AxiosResponse<ILineItemRemoveSuccessPayload>) => {
            if (state$.value.checkout.selection.pluginFields && isShipwallet2(state$.value.checkout.selection.pluginFields.shipwallet)) {
              ingridResume();
            }
            kcoResume();
            if (payload && payload.data && payload.data.selection && state$.value && state$.value.checkout && state$.value.checkout.selection) {
              try {
                eeRemoveFromCart(payload.data.selection.items, state$.value.checkout.selection.items, payload.data.selection.currency);
              } catch (err) {
                // tslint:disable-next-line:no-console
                console.error('Enhanced Ecommerce error: ', err);
              }
            }
            const { paymentMethod: selectedPaymentMethod } = state$.value.checkout.selection;
            const isAdyenCheckoutSelected = isAdyenCheckout(selectedPaymentMethod);

            const adyenOfs = isAdyenCheckoutSelected ? [of$(checkoutRequest())] : [];

            // This is for age limit functionality (needs to replace KCO snipped in some cases)
            if (payload.data && payload.data.selection && payload.data.selection.pluginFields &&
                payload.data.selection.pluginFields.klarnaReplaceSnippet !== undefined &&
                payload.data.selection.pluginFields && payload.data.selection.pluginFields.klarnaReplaceSnippet &&
                payload.data.selection.pluginFields && payload.data.selection.pluginFields.paymentHTML) {

              if (window.location.pathname.includes('selection') && isAdyenCheckoutSelected) {
                return concat$(
                  of$(fetchKCOWidgetSuccess(Object.assign({}, { formHtml: payload.data.selection.pluginFields.paymentHTML }, { selectedPaymentMethod }))),
                  of$(removeLineItemSuccess(payload.data)),
                  of$(checkoutRequest()),
                  ...adyenOfs
                );
              }

              return concat$(
                of$(fetchKCOWidgetSuccess(Object.assign({}, { formHtml: payload.data.selection.pluginFields.paymentHTML }, { selectedPaymentMethod }))),
                of$(removeLineItemSuccess(payload.data))
              );
            }

            if (window.location.pathname.includes('selection') && isAdyenCheckoutSelected) {
              return concat$(
                of$(removeLineItemSuccess(payload.data)),
                of$(checkoutRequest()),
                ...adyenOfs
              );
            }

            return concat$(of$(removeLineItemSuccess(payload.data)));
          }),
          catchError((error: IError) => {
            kcoResume();
            ingridResume();
            logger(error);
            // TODO - if "404 Not Found" clear selection or this specific item ?
            return of$(setErrors(error));
          })
        )
    )
);

/*
 * Reseting errors after expiration date error in minicart (overlay didn't close after adding to cart)
 */
export const resetErrorsEpic: Epic<RootAction, RootAction, RootState> = (action$, state$): Observable<RootAction> =>
  action$.pipe(
    ofType(
    ActionTypes.ITEM_ADD_REQUEST,
    ActionTypes.LINE_ITEM_ADD_REQUEST
  ),
  filter(() => state$.value.checkout.errors !== undefined),
  mergeMap((action) => of$(resetErrors()))
);
